/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import textColor32 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/text-color_32';
import { propsUtils, lodash } from '@ba-ui-toolkit/utils';
import { Flyout } from '@ba-ui-toolkit/flyout';
import Button from '@ba-ui-toolkit/button';
import styles from './ColorPicker.scss';
import stringResource from './nls/StringResource';

const colorpickerClassName = styles.colorpickerClassName.replace(/"/g, '');
const { sanitize } = propsUtils;
const { uniqueId } = lodash;

export default class ColorPicker extends Component {
	static propTypes = {
		/** Colors to be selected */
		colors: PropTypes.array,
		/** Function that will get called on click of a color */
		onColorSelected: PropTypes.func,
		/** Selected color to show at the bottom of button */
		selectedColor: PropTypes.string,
		/** Flag to disable the color picker */
		disabled: PropTypes.bool,
		/** Tooltip when you hover on the color picker */
		title: PropTypes.string,
		/** Flyout.propTypes.placement */
		orientation: Flyout.propTypes.placement,
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom css styles */
		style: PropTypes.object,
		/** Extra options for the button to pass to Flyout */
		buttonExtraOptions: PropTypes.object,
		/** Extra options to pass to Flyout */
		flyoutExtraOptions: PropTypes.object,
	};

	static defaultProps = {
		disabled: false,
		title: stringResource.get('title'),
		className: '',
		style: {},
		buttonExtraOptions: {},
		flyoutExtraOptions: {},
	};

	constructor(props) {
		super(props);
		this.state = {
			open: false,
		};
		this._handleOpen = this._handleOpen.bind(this);
		this._handleSelect = this._handleSelect.bind(this);
		this._handleKey = this._handleKey.bind(this);
		this._id = uniqueId('ba-common-color-picker__');
	}

	_handleOpen() {
		this.setState({ open: !this.state.open });
	}

	_handleSelect(color) {
		this.props.onColorSelected(color);
		// close the flyout after selection
		this.setState({ open: !this.state.open });
	}

	_handleKey(e, color) {
		// 13 = Return, 32 = Space
		if (e.keyCode === 13 || e.keyCode === 32) {
			this._handleSelect(color);
			// Prevent browser's default action: space does a PageDown
			e.preventDefault();
		}
	}

	_displayColor = (color, index) => (
		<div
			key={index}
			tabIndex="0" // Make it keyboard focusable
			style={{ background: color }}
			className={`${colorpickerClassName}__cell`}
			onClick={() => this._handleSelect(color)}
			onKeyDown={e => this._handleKey(e, color)}
		/>
	);

	render() {
		const { open } = this.state;
		const {
			selectedColor,
			disabled,
			title,
			className,
			style,
			buttonExtraOptions,
			flyoutExtraOptions,
			...others
		} = this.props;
		let { orientation } = this.props;
		if (typeof orientation === 'undefined') {
			orientation = document.body.dir === 'rtl' ? 'bottomLeft' : 'bottomRight';
		}

		return (
			<div
				{...sanitize(others, ColorPicker)}
				className={`${colorpickerClassName} ${className}`}
				ref={node => {
					this._colorPickerNode = node;
				}}
				style={style}
			>
				<Button
					aria-controls={`${this._id}-flyout`}
					data-tid="ba-common-color-picker__button"
					{...sanitize(buttonExtraOptions, ColorPicker)}
					title={title}
					disabled={disabled}
					icon={textColor32.id}
					ref="myButton"
					onClick={this._handleOpen}
					variant="frameless"
					className={`${colorpickerClassName}__button`}
				>
					{/* a hack way to change the colorbar */}
					{!disabled && (
						<span
							style={{ background: selectedColor }}
							className={`${colorpickerClassName}__colorbar`}
						/>
					)}
				</Button>
				{open && (
					<Flyout
						id={`${this._id}-flyout`}
						data-tid="ba-common-color-picker__flyout"
						{...flyoutExtraOptions}
						aria-hidden={open}
						placement={orientation}
						triggerNode={this.refs.myButton._node}
						domNodeToAttachTo={this._colorPickerNode}
						hideArrow
						offset={2}
						onClose={this._handleOpen}
						className={`${colorpickerClassName}__flyout`}
					>
						{this.props.colors.map(this._displayColor)}
					</Flyout>
				)}
			</div>
		);
	}
}
